import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { specialArticles } from "@/data/specials";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SpecialArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = specialArticles.find((item) => item.slug === slug);

  usePageMeta({
    title: article ? `${article.title} - 色戒深度解读专题` : "专题文章不存在 - 色戒无删减版",
    description: article
      ? `${article.subtitle}。本文属于色戒电影深度解读专题的${article.group}栏目，约 ${article.readTime}阅读。`
      : "你访问的专题文章不存在，请返回专题合辑重新选择。",
    keywords: article
      ? `${article.title},色戒${article.group},色戒深度解读,色戒无删减版`
      : "色戒无删减版,色戒专题",
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="container-page flex min-h-[70vh] flex-col items-center justify-center py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-accent-500/30 bg-accent-500/10 text-accent-400">
            <i className="ri-article-line text-3xl leading-none" />
          </span>
          <h1 className="mt-6 font-heading text-2xl text-foreground-950">这篇文章暂时找不到了</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            也许链接有误，或者专题仍在整理中。回到专题合辑可以看到全部六篇文章。
          </p>
          <Link
            to="/special"
            className="mt-7 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 font-label text-sm font-medium text-background-50"
          >
            返回专题合辑
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const others = specialArticles.filter((item) => item.slug !== article.slug).slice(0, 4);
  const groupArticles = specialArticles.filter(
    (item) => item.group === article.group && item.slug !== article.slug,
  );

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <section className="relative overflow-hidden border-b border-background-200 pb-10 pt-24 md:pb-14 md:pt-36">
          <div className="absolute inset-0">
            <img
              src={article.cover}
              alt={`${article.title} 文章背景`}
              className="h-full w-full object-cover object-top opacity-50"
            />
            <div className="absolute inset-0 bg-background-50/88" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/40" />
          </div>

          <div className="container-page relative">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "专题", to: "/special" },
                { label: article.group, to: "/special" },
                { label: article.title },
              ]}
            />
            <div className="mt-6 max-w-3xl">
              <div className="flex flex-wrap items-center gap-2 font-label text-[11px] text-foreground-500">
                <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-secondary-900">{article.group}</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}阅读</span>
                <span>·</span>
                <span>原创整理</span>
              </div>
              <h1 className="mt-4 font-heading text-3xl leading-tight text-foreground-950 md:text-[42px]">
                {article.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{article.subtitle}</p>
            </div>
          </div>
        </section>

        <div className="container-page py-10 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
            <article className="min-w-0">
              <p className="border-l-2 border-accent-500 pl-4 text-base leading-[1.95] text-foreground-800 md:text-lg">
                {article.paragraphs[0]}
              </p>

              <div className="mt-8 space-y-5">
                {article.paragraphs.slice(1).map((paragraph, index) => (
                  <p key={index} className="text-sm leading-[2] text-foreground-600 md:text-[15px]">
                    {paragraph}
                  </p>
                ))}
              </div>

              <blockquote className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                <i className="ri-double-quotes-l text-2xl leading-none text-accent-500/60" />
                <p className="mt-3 font-heading text-lg leading-relaxed text-foreground-900 md:text-xl">
                  真正让人难受的，不是谁输谁赢，而是有些话永远没有机会说清楚。
                </p>
                <footer className="mt-3 font-label text-[11px] tracking-[0.18em] text-foreground-500">
                  —— 本站专题编辑部
                </footer>
              </blockquote>

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="font-label text-[11px] tracking-[0.18em] text-foreground-500">本文标签</span>
                {[article.group, "剧情解析", "镜头语言", "角色动机"].map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-secondary-100 px-2.5 py-1 font-label text-[11px] text-secondary-900"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {groupArticles.length > 0 && (
                <section className="mt-10">
                  <h2 className="font-heading text-lg text-foreground-950">同栏目文章</h2>
                  <div className="mt-4 flex flex-col divide-y divide-background-200 border-y border-background-200">
                    {groupArticles.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/special/${item.slug}`}
                        className="group flex items-center justify-between gap-4 py-4"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-heading text-sm text-foreground-900 transition-colors group-hover:text-accent-400">
                            {item.title}
                          </p>
                          <p className="mt-1 truncate text-xs text-foreground-500">{item.subtitle}</p>
                        </div>
                        <i className="ri-arrow-right-line shrink-0 text-base leading-none text-foreground-500 group-hover:text-accent-400" />
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>

            <aside className="flex flex-col gap-5">
              <div className="surface-card p-5">
                <h2 className="font-heading text-sm text-foreground-950">更多专题文章</h2>
                <div className="mt-4 flex flex-col divide-y divide-background-200">
                  {others.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/special/${item.slug}`}
                      className="group flex items-start gap-3 py-3 first:pt-0 last:pb-0"
                    >
                      <div className="h-14 w-16 shrink-0 overflow-hidden rounded-md border border-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title} 缩略图`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-xs leading-snug text-foreground-900 transition-colors group-hover:text-accent-400">
                          {item.title}
                        </p>
                        <p className="mt-1 font-label text-[11px] text-foreground-500">{item.group}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/special"
                  className="mt-4 inline-flex items-center gap-1 font-label text-xs text-accent-500 hover:text-accent-400"
                >
                  查看全部专题
                  <i className="ri-arrow-right-line text-sm leading-none" />
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <p className="font-label text-[11px] tracking-[0.18em] text-accent-500">边读边看</p>
                <h3 className="mt-2 font-heading text-base text-foreground-950">回到章节，对照剧情</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  文中提到的牌局、珠宝店与封锁线，都能在对应章节找到位置。
                </p>
                <Link
                  to="/detail/chapter-04"
                  className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-xs text-foreground-950 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line text-sm leading-none" />
                  前往第 4 章
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}